import React from "react";
import examImg from "@assets/exam.svg";
import tutorialImg from "@assets/tutorial.svg";
import {
  Box,
  BoxProps,
  Collapse,
  Flex,
  Heading,
  Img,
  Link,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { FiChevronDown } from "@react-icons/all-files/fi/FiChevronDown";
import { FiChevronUp } from "@react-icons/all-files/fi/FiChevronUp";
import { Link as GatsbyLink } from "gatsby";
import { useButtonColor } from "@utils";

const COURSE_DETAILS = {
  img: tutorialImg,
  blurb: "Take your time, learn as you go",
  heading: "Tutorial series",
  description:
    "Become automatically certified when you complete all of the courses in this series.",
  linkText: "Complete the tutorials",
  hasLearningPath: true,
};

const EXAM_DETAILS = {
  img: examImg,
  blurb: "Quickly validate your knowledge",
  heading: "Certification exam",
  description:
    "Looking for a speedier solution? Become certified today by taking our exam!",
  linkText: "Take the exam",
};

export const CertificationLearningPath: React.FC<{
  exam: Pick<Queries.Exam, "slug"> | null;
  courses: LearningPathCourses;
}> = ({ courses, exam }) => {
  if (exam) {
    return (
      <SimpleGrid alignItems="flex-start" columns={{ md: 2 }} spacing="6">
        <LearningPathCard {...COURSE_DETAILS} courses={courses} />
        <LearningPathCard {...EXAM_DETAILS} examSlug={exam.slug} />
      </SimpleGrid>
    );
  }

  return (
    <LearningPathCard
      {...COURSE_DETAILS}
      courses={courses}
      maxW="580px"
      mx="auto"
    />
  );
};

const LearningPathCard: React.FC<
  {
    img: string;
    blurb: string;
    heading: string;
    description: string;
    linkText: string;
    hasLearningPath?: boolean;
    courses?: LearningPathCourses;
    examSlug?: string;
  } & BoxProps
> = ({
  img,
  blurb,
  heading,
  description,
  linkText,
  hasLearningPath,
  courses,
  examSlug,
  ...props
}) => {
  const linkColor = useButtonColor("blue");

  return (
    <Box
      bg="blue.200"
      _dark={{ bg: "black.200" }}
      rounded="lg"
      py="10"
      px={{ base: 4, md: 6 }}
      textAlign="center"
      {...props}
    >
      <Flex w="full" mb="6" justifyContent="center">
        <Img src={img} alt={heading} maxW={200} />
      </Flex>
      <VStack spacing="2">
        <Box
          as="span"
          fontFamily="mono"
          fontSize="sm"
          fontWeight="semibold"
          textTransform="uppercase"
        >
          {blurb}
        </Box>
        <Heading>{heading}</Heading>
        <Text maxW="280px">{description}</Text>
      </VStack>

      {hasLearningPath && (
        <LearningPath
          courses={courses ?? []}
          linkText={linkText}
          linkColor={linkColor}
        />
      )}

      {examSlug && (
        <Box mt="8">
          <Link
            as={GatsbyLink}
            to={`/exams/${examSlug}`}
            color={linkColor}
            fontWeight="semibold"
          >
            {linkText}
          </Link>
        </Box>
      )}
    </Box>
  );
};

type LearningPathCourses = readonly Pick<
  Queries.Course,
  "id" | "title" | "description" | "slug"
>[];

const LearningPath: React.FC<{
  courses: LearningPathCourses;
  linkText: string;
  linkColor: string;
}> = ({ courses, linkText, linkColor }) => {
  const { isOpen, onToggle } = useDisclosure();
  const bgColor = useColorModeValue("white", "whiteAlpha.200");

  return (
    <>
      <Flex justifyContent="space-evenly" w="full" mt="8">
        <Link
          as={GatsbyLink}
          to={`/${courses[0].slug}`}
          color={linkColor}
          fontWeight="semibold"
        >
          {linkText}
        </Link>
        <Flex
          as="button"
          _hover={{
            textDecoration: "underline",
          }}
          justifyContent="space-evenly"
          color={linkColor}
          fontWeight="semibold"
          alignItems="center"
          onClick={onToggle}
        >
          Learning path
          <Box as={isOpen ? FiChevronUp : FiChevronDown} ml="3" />
        </Flex>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <VStack spacing="3" w="full" mt="8">
          {courses.map((course) => (
            <Box
              key={course.id}
              bgColor={bgColor}
              w="full"
              p="4"
              textAlign="left"
              rounded="md"
            >
              <Link as={GatsbyLink} to={`/${course.slug}`}>
                <Heading as="h3" fontSize="lg">
                  {course.title}
                </Heading>
              </Link>
              <Text>{course.description}</Text>
            </Box>
          ))}
        </VStack>
      </Collapse>
    </>
  );
};
